import { useState } from 'react';
import { Wallet, TokenAsset, CronosChain, EthChainId } from '@types';
import { generateSigner } from '@lib/utils';
import { getExtraInfo as getCronosExtraInfo } from '@lib/cronos-chain';
import {
  getErc20Contract,
  getErc20Approval,
  getErc20ApprovalGasEstimate,
} from '@lib/gravity-bridge';
import useNetworkEnv from './useNetworkEnv';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';

interface useGetERC20ApprovalParams {
  wallet: Wallet;
  tokenAsset: TokenAsset | null;
}

function useGetERC20Approval({
  wallet,
  tokenAsset,
}: useGetERC20ApprovalParams) {
  const [ERC20Approved, setERC20Approved] = useState(false);
  const [ERC20Approving, setERC20Approving] = useState(false);
  const [approvalError, setApprovalError] = useState<unknown | null>(null);
  const [isTestnet] = useNetworkEnv();

  const getERC20Approval = async () => {
    const maxAmount =
      '115792089237316195423570985008687907853269984665640564039457584007913129639935';

    try {
      setERC20Approving(true);

      const contractAddress = getCronosExtraInfo(
        isTestnet ? EthChainId.CronosGravityTestnet : EthChainId.Cronos
      ).contractAddresses.gravity;
      const signer = await generateSigner(wallet);
      const erc20Contract = getErc20Contract(
        tokenAsset!.address[wallet.chain],
        signer
      );

      const gasAmount = await getErc20ApprovalGasEstimate(
        contractAddress,
        maxAmount,
        wallet.address,
        erc20Contract
      );

      const tx = await getErc20Approval(
        contractAddress,
        maxAmount,
        wallet.address,
        gasAmount,
        erc20Contract
      );
      const { status } = await tx.wait();
      if (status !== 0) setERC20Approved(true);
    } catch (error) {
      notify(error as NotifiableError);
      setApprovalError(error);
    } finally {
      setERC20Approving(false);
    }
  };

  return {
    ERC20Approved,
    ERC20Approving,
    getERC20Approval,
    approvalError,
  };
}

export default useGetERC20Approval;
